import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { cuppingSessionService } from '../../services/cuppingSessionService';
import type { CuppingSessionStatus } from '../../types/cuppingSession';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { CuppingSessionCreateModal } from './components/CuppingSessionCreateModal';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';

export const CuppingSessionsPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('cupping_sessions', 'write');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<CuppingSessionStatus | 'all'>('all');

  const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([searchFilter, dateFilter, statusFilter]);

  const { data: sessionsResponse, isLoading } = useQuery({
    queryKey: ['cupping-sessions', { page, size, searchFilter, dateFilter, statusFilter }],
    queryFn: () => cuppingSessionService.list({
      page,
      size,
      search: searchFilter.trim() || undefined,
      date: dateFilter || undefined,
      status: statusFilter,
    }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const sessions = sessionsResponse?.data ?? [];
  const totalSessions = sessionsResponse?.total ?? 0;

  const { data: allSessions = [] } = useQuery({
    queryKey: ['cupping-sessions', 'all'],
    queryFn: () => cuppingSessionService.getAll(),
  });

  // Server handles filtering; sessions already paginated.
  const filteredSessions = sessions;

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('cuppingSessions.title')}</h1>
        {canWrite && (
          <button
            data-test-id="cupping-session-new-button"
            onClick={() => setIsCreateOpen(true)}
            className="w-full sm:w-auto inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 shadow-sm"
          >
            <Plus className="h-4 w-4" />
            {t('cuppingSessions.actions.new')}
          </button>
        )}
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">{t('purchasing.sampleList.filters.search')}</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                data-test-id="cupping-session-search-input"
                type="text"
                placeholder={t('cuppingSessions.searchPlaceholder')}
                value={searchFilter}
                onChange={(event) => setSearchFilter(event.target.value)}
                className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
            </div>
          </div>
          <div className="w-full sm:w-52">
            <label className="block text-sm font-medium text-gray-700">{t('reports.general.table.date')}</label>
            <input
              data-test-id="cupping-session-date-filter"
              type="date"
              value={dateFilter}
              onChange={(event) => setDateFilter(event.target.value)}
              onClick={openNativeDatePickerSafely}
              onKeyDown={preventManualDateEntry}
              onPaste={preventManualDatePasteOrDrop}
              onDrop={preventManualDatePasteOrDrop}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            />
          </div>
          <div className="w-full sm:w-48">
            <label className="block text-sm font-medium text-gray-700">{t('forms.common.status')}</label>
            <Listbox value={statusFilter} onChange={setStatusFilter}>
              <div className="relative mt-1">
                <ListboxButton
                  data-test-id="cupping-session-status-filter"
                  className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
                >
                  <span className="block truncate">{statusFilter === 'all' ? t('salesOrder.list.status.all') : t(`cuppingSessions.status.${statusFilter}`, undefined, statusFilter)}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-10 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {['all', 'Draft', 'Complete'].map((status) => (
                    <ListboxOption
                      key={status}
                      value={status as CuppingSessionStatus | 'all'}
                      className={({ focus }: { focus: boolean }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'}`
                      }
                    >
                      {({ selected }: { selected: boolean }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {status === 'all' ? t('salesOrder.list.status.all') : t(`cuppingSessions.status.${status}`, undefined, status)}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg border border-gray-200">
        {isLoading ? (
          <LoadingSpinner text={t('cuppingSessions.loading')} />
        ) : filteredSessions.length === 0 ? (
          <div className="px-4 py-5 sm:p-6 text-center text-gray-500 italic">
            {t('cuppingSessions.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" data-test-id="cupping-session-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('cuppingSessions.columns.code')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('cuppingSessions.columns.cupper')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('cuppingSessions.columns.items')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.status')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('cuppingSessions.columns.draftDate')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('cuppingSessions.columns.completeDate')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('settingsMcp.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSessions.map((session) => (
                  <tr key={session.id} data-test-id={`cupping-session-row-${session.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{session.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{session.cupperName || t('settingsMcp.values.none')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">
                      {session.details?.length ?? 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          session.status === 'Complete'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {t(`cuppingSessions.status.${session.status}`, undefined, session.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">{formatDate(session.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">{formatDate(session.completedAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        data-test-id={`cupping-session-open-${session.id}`}
                        onClick={() => navigate(`/cupping-sessions/${session.id}`)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        {t('cuppingSessions.actions.open')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} pageSize={size} total={totalSessions} onPageChange={setPage} onPageSizeChange={setSize} isLoading={isLoading} />
      </div>

      <CuppingSessionCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(session) => {
          setIsCreateOpen(false);
          navigate(`/cupping-sessions/${session.id}`);
        }}
        existingCount={allSessions.length}
      />
    </div>
  );
};
